"use client";

/* "use client" DIPERLUKAN, dan biayanya NOL.

   Berkas ini memakai useState, useEffect, dan useRef untuk menyalakan
   goyangan di perangkat sentuh, jadi ia wajib jadi Client Component.

   Yang biasanya jadi keberatan - "berarti komponennya ikut dikirim
   sebagai JavaScript ke browser" - tidak berlaku di sini. page.jsx
   sudah menulis "use client" di baris pertamanya, dan di App Router
   setiap komponen yang diimpor oleh Client Component OTOMATIS ikut
   masuk bundel klien. Facility sudah berada di sisi klien sejak
   sebelum perubahan ini; baris ini hanya menuliskan yang sudah
   terjadi.

   Kalau suatu saat page.jsx berhasil dikembalikan jadi Server
   Component, barulah biaya itu nyata dan pantas ditimbang ulang.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

/* ============================================================
   FACILITY - LINGKARAN MENGAMBANG

   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<

   Versi awal berkas ini menulis className begini supaya enak dibaca:

       className="
         relative mx-auto aspect-square
         rounded-full
       "

   Itu menyebabkan hydration error. String tersebut ikut menyimpan
   karakter akhir baris, dan karena project ini memakai akhiran baris
   CRLF (Windows), isinya mengandung \r\n. Bundel server dan bundel
   klien menormalkan spasi putih itu dengan cara berbeda, sehingga nilai
   atribut class yang dikirim server tidak sama persis dengan yang
   dihasilkan klien. React membandingkan keduanya karakter per karakter
   dan melaporkannya sebagai ketidakcocokan.

   Semua className di berkas ini ditulis satu baris. Panjang, tapi itu
   memang cara normal menulis Tailwind - dan ia bekerja.
   ============================================================ */

/* ============================================================
   ⚠️ POKOK PERSOALAN: EFEK HOVER TIDAK BISA "IKUT" KE PONSEL

   Ini perlu ditulis terang-terangan karena tidak bisa ditambal, dan
   siapa pun yang menyentuh berkas ini nanti akan bertanya hal yang
   sama.

   :hover berarti "penunjuk sedang berada di atas elemen". Jari tidak
   melayang - ia menyentuh lalu lepas. Di layar sentuh, keadaan "kursor
   sedang di dalam kartu" TIDAK PERNAH ADA, jadi tidak ada cara membuat
   efek hover berjalan di sana. Yang bisa dilakukan hanyalah memilih
   PEMICU LAIN yang masuk akal untuk jari.

   TIGA PEMICU, dipetakan ke tiga cara orang memakai perangkatnya:

   1. KURSOR MASUK (komputer)  -> :hover, lewat group-hover:*
      Semua efek jalan penuh. Goyangan berputar tanpa henti selama
      kursor masih di dalam kartu.

   2. KETUKAN (ponsel/tablet)  -> onClick, lewat .fasilitas-goyang
      Goyangan main 3 putaran lalu berhenti. Bisa diketuk berkali-kali;
      setiap ketukan mengulangnya dari awal.

   3. JARI MENEKAN (semua)     -> :active, lewat group-active:*
      Terangkat + membesar selama jari masih menempel. Umpan balik
      seketika, sebelum goyangannya sempat jalan.

   YANG DIHAPUS DARI VERSI SEBELUMNYA: pemicu "kartu masuk layar".
   Dulu goyangannya main sendiri sekali saat kartunya tergulir masuk,
   dan itu justru membingungkan - gerakan yang terjadi tanpa ada yang
   menyentuh apa pun terbaca sebagai gangguan, bukan umpan balik. Yang
   diminta adalah efek saat KARTUNYA DISENTUH, dan itu yang sekarang
   dikerjakan.

   YANG SENGAJA TIDAK DILAKUKAN: menjalankan goyangan tak terhingga di
   ponsel. Di komputer ia berhenti karena pengunjung menggerakkan
   kursor keluar - kendali ada di tangannya. Di ponsel tidak ada
   tindakan setara, jadi tak terhingga berarti kartu bergetar selamanya
   sampai halaman ditinggalkan. Di halaman rumah sakit itu juga memicu
   vertigo pada sebagian pengunjung.

   BINGKAI DAN BAYANGANNYA SENDIRI SUDAH SAMA DI SEMUA PERANGKAT.
   Keduanya tidak bergantung pada hover: lingkaran, cincin matte,
   bayangan tanah, dan proporsinya ditulis dengan satuan relatif
   sehingga menyusut mengikuti lebar kolom.
   ============================================================ */

/* ============================================================
   BENTUK

   Lingkaran penuh. Tidak ada lagi utilitas sudut tunggal, dan tidak
   ada lagi perpindahan sudut saat hover.

   transition-all tetap dipertahankan meski tidak ada radius yang
   beranimasi lagi, karena ia juga yang menjalankan perubahan ring,
   bayangan, dan translate.
   ============================================================ */

const KELOPAK =
  "rounded-full transition-all duration-500 ease-out motion-reduce:transition-none";

/* ============================================================
   KOMPENSASI FOTO BUNDAR - BACA SEBELUM MENGUBAH

   ⚠️ NILAINYA false, DAN ITU DISENGAJA. Kalau dikembalikan ke true,
   fotonya kembali buram tanpa memperbaiki apa pun.

   RIWAYATNYA. Beberapa berkas di /public/images adalah potongan BUNDAR
   ber-PNG dengan sudut transparan. Selama bingkainya berbentuk kelopak
   - lingkaran dengan SATU SUDUT SIKU - sudut siku itu menganga: ia
   berjarak 0,707 sisi dari pusat, sementara lingkaran fotonya hanya
   menjangkau 0,5. Selisih itulah baji hitam yang dulu terlihat di
   kanan-bawah setiap kartu.

   Tambalannya dulu: perbesar foto 1,42x (akar 2, karena 0,707 dibagi
   0,5 tepat sama dengan akar 2). Harganya mahal - sekitar 30% tepi
   foto terbuang dan gambarnya ditarik melebihi ukuran asli.

   KENAPA TIDAK PERLU LAGI. Bingkainya lingkaran penuh, jadi TIDAK ADA
   SUDUT yang perlu ditutup. Topeng lingkaran milik bingkai dan
   lingkaran milik foto adalah lingkaran yang sama persis - sudut
   transparan PNG-nya jatuh di luar topeng dan tidak pernah terlihat.

   Sisa 1,03x bukan kompensasi, hanya jaminan 3% supaya tepi foto tidak
   menyisakan garis rambut akibat pembulatan piksel dan antialiasing.

   ⚠️ KALAU BENTUKNYA DIKEMBALIKAN KE KELOPAK, setel konstanta ini ke
   true lagi - kalau tidak, baji hitam di sudut akan muncul kembali.
   ============================================================ */

const KOMPENSASI_FOTO_BUNDAR = false;

/* Ditulis sebagai string class utuh, bukan dirakit dari potongan.
   Tailwind memindai berkas sebagai teks biasa; class yang disusun dari
   variabel saat runtime tidak akan ikut ter-generate. */
const ZOOM_DASAR = KOMPENSASI_FOTO_BUNDAR ? "scale-[1.42]" : "scale-[1.03]";

/* Dua varian untuk keadaan yang sama. group-hover melayani kursor,
   group-active melayani jari yang sedang menekan. Nilainya sengaja
   identik supaya kedua perangkat melihat hal yang sama. */
const ZOOM_AKTIF = KOMPENSASI_FOTO_BUNDAR
  ? "group-hover:scale-[1.53] group-active:scale-[1.53]"
  : "group-hover:scale-[1.12] group-active:scale-[1.12]";

/* ============================================================
   BAYANGAN BERLAPIS

   Dua lapis, bukan satu:
     contact  garis rapat, membuat kartu menempel ke permukaan
     ambient  halo lebar, memberi kesan terangkat

   Warna dasarnya rgba(18,22,26) - plum gelap, BUKAN hitam. Bayangan
   hitam pekat di atas bidang putih terbaca keabu-abuan dan kotor.
   Angkanya disamakan dengan SHADOW_CARD di page.jsx.
   ============================================================ */

const BAYANGAN =
  "shadow-[0_1px_2px_rgba(18,22,26,0.06),0_10px_28px_-10px_rgba(18,22,26,0.18)]";

const BAYANGAN_AKTIF =
  "group-hover:shadow-[0_3px_8px_rgba(18,22,26,0.10),0_22px_50px_-12px_rgba(18,22,26,0.32)] group-active:shadow-[0_3px_8px_rgba(18,22,26,0.10),0_22px_50px_-12px_rgba(18,22,26,0.32)]";

export default function Facility({ title, img }) {
  /* ============================================================
     PEMICU GOYANGAN UNTUK KETUKAN

     Dimulai false di server MAUPUN di klien. Itu disengaja: nilai
     yang berbeda antara kedua render adalah penyebab klasik
     hydration error, dan kelas CSS ikut terbandingkan karakter per
     karakter. Kelasnya baru ditambahkan setelah ada yang mengetuk.
     ============================================================ */
  const [goyang, setGoyang] = useState(false);
  const rafRef = useRef(0);

  /* ============================================================
     KENAPA HARUS DILEPAS DULU BARU DIPASANG LAGI

     Ini jebakan yang membuat ketukan KEDUA tidak menghasilkan apa-apa
     kalau ditulis dengan cara yang tampak paling wajar.

     CSS hanya menjalankan animasi ketika kelasnya BERUBAH dari tidak
     ada menjadi ada. Kalau kelasnya sudah terpasang dan kita
     memasangnya lagi, tidak ada yang berubah - browser menganggap
     tidak ada perintah baru, dan animasinya tidak diulang.

     Karena itu urutannya: lepas dulu (setGoyang false), tunggu sampai
     browser benar-benar menggambar keadaan tanpa kelas itu, baru
     pasang lagi.

     DUA LAPIS requestAnimationFrame, bukan satu. Satu rAF hanya
     menjamin kita berada di frame berikutnya - React bisa saja
     menggabungkan false dan true jadi satu pembaruan, dan hasilnya
     kelasnya tidak pernah benar-benar hilang. Lapis kedua menjamin
     penghapusan itu sudah tergambar sebelum pemasangannya menyusul.

     Ini pola baku untuk mengulang animasi CSS. Alternatif klasiknya
     memaksa reflow dengan membaca el.offsetWidth, tapi cara itu
     menyentuh DOM langsung di luar sepengetahuan React.
     ============================================================ */
  const picuGoyangan = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setGoyang(false);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setGoyang(true));
    });
  }, []);

  /* Membatalkan frame yang masih menunggu kalau komponennya dilepas
     di tengah jalan - misalnya saat pengunjung berpindah halaman
     tepat setelah mengetuk. Tanpa ini, React memperingatkan adanya
     pembaruan state pada komponen yang sudah tidak ada. */
  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <figure
      onClick={picuGoyangan}
      className={["group", "fasilitas-kartu", goyang ? "fasilitas-goyang" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="relative mx-auto aspect-square w-full">
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-[12%] bottom-[-3%] h-[22%] rounded-[50%] bg-[var(--c-panel-deep)]/20 opacity-80 blur-lg transition-all duration-500 ease-out group-hover:scale-110 group-hover:bg-[var(--c-panel-deep)]/30 group-hover:opacity-100 group-active:scale-110 group-active:opacity-100 motion-reduce:transition-none motion-reduce:group-hover:scale-100 motion-reduce:group-active:scale-100 sm:blur-xl md:blur-2xl" />

        <div className={`absolute inset-0 overflow-hidden bg-[var(--c-surface)] p-1.5 ring-1 ring-[var(--c-line)] group-hover:-translate-y-1 group-hover:ring-2 group-hover:ring-[var(--c-brand)] group-active:-translate-y-1 group-active:ring-2 group-active:ring-[var(--c-brand)] motion-reduce:group-hover:translate-y-0 motion-reduce:group-active:translate-y-0 sm:p-2 ${BAYANGAN} ${BAYANGAN_AKTIF} ${KELOPAK}`}>
          <div className={`relative h-full w-full overflow-hidden ${KELOPAK}`}>

            <div
              className="fasilitas-foto absolute inset-0"
              onAnimationEnd={() => setGoyang(false)}
            >
              <Image
                src={img}
                fill

                sizes="(min-width: 1024px) 420px, (min-width: 768px) 200px, 50vw"
                alt={title}
                className={`object-cover transition-transform duration-700 ease-out motion-reduce:transition-none ${ZOOM_DASAR} ${ZOOM_AKTIF}`}
              />
            </div>

          </div>
        </div>
      </div>

      <figcaption className="mt-3 sm:mt-4 md:mt-5">
        <p className="text-[13px] font-bold leading-snug text-[var(--c-ink)] transition-colors duration-500 group-hover:text-[var(--c-brand)] group-active:text-[var(--c-brand)] motion-reduce:transition-none sm:text-sm md:text-base lg:text-lg">
          {title}
        </p>
      </figcaption>

    </figure>
  );
}
